import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './Button';
import { useEmbedded } from './Embed';
import { Callout, Skeleton } from './Feedback';

/**
 * Section scaffolding for settings and detail pages: a section is a heading,
 * a description and blocks separated by hairlines — no cards.
 *
 * Three headers, smallest first:
 * - `SectionTitle`    12px label inside a `Panel` body.
 * - `SectionTitleRow` 13px sub-heading for a list beside the main content.
 * - `SectionHead`     16px heading for a whole page section.
 */

/** Small uppercase-free section header used inside panels. */
export const SectionTitle: React.FC<{
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ children, action, className }) => (
  <div className={clsx('flex h-6 items-center justify-between', className)}>
    <h3 className="text-v2-12 font-semibold text-v2-fg-3">{children}</h3>
    {action}
  </div>
);

/** Sub-heading for a list beside the main content: label · count · action. */
export const SectionTitleRow: React.FC<{
  title: string;
  count?: number;
  action?: React.ReactNode;
}> = ({ title, count, action }) => (
  <div className="flex h-7 items-center justify-between">
    <h3 className="text-v2-13 font-semibold text-v2-fg">
      {title}
      {typeof count === 'number' && (
        <span className="v2-num ml-1.5 font-normal text-v2-fg-3">{count}</span>
      )}
    </h3>
    {action}
  </div>
);

/** Section heading: 16px title, optional one-line description, optional right action. */
export const SectionHead: React.FC<{
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, description, action }) => {
  // Embedded, the host shows the title; keep the heading for screen readers.
  const embedded = useEmbedded();
  if (embedded && !description && !action) return null;
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        <h2
          className={clsx(
            embedded ? 'sr-only' : 'text-v2-16 font-semibold text-v2-fg',
          )}
        >
          {title}
        </h2>
        {description && (
          <p
            className={clsx(
              'max-w-[68ch]',
              // Embedded in a step, it is the step's intro, so it matches one.
              embedded
                ? 'text-v2-13 leading-5 text-v2-fg-2'
                : 'mt-0.5 text-v2-12 leading-[18px] text-v2-fg-3',
            )}
          >
            {description}
          </p>
        )}
      </div>
      {action && (
        <div className="flex shrink-0 items-center gap-2">{action}</div>
      )}
    </div>
  );
};

/**
 * Block: one topic inside a section (Agent voice, Call intro…). Blocks stack
 * with a hairline between them. The right slot holds the block's own control
 * (a `Switch`, an Add button) — never a Save button; saving is the `SaveBar`.
 */
export const Block: React.FC<{
  title: string;
  description?: React.ReactNode;
  control?: React.ReactNode;
  children: React.ReactNode;
  first?: boolean;
  /** Quieter heading for a supporting configuration block. */
  quiet?: boolean;
  className?: string;
  titleClassName?: string;
}> = ({
  title,
  description,
  control,
  children,
  first,
  quiet,
  className,
  titleClassName,
}) => (
  <section
    aria-label={title}
    className={clsx(!first && 'mt-7 border-t border-v2-line pt-6', className)}
  >
    <div
      className={clsx(
        'flex justify-between gap-4',
        description ? 'items-start' : 'items-center',
      )}
    >
      <div className="min-w-0">
        <h3
          className={clsx(
            quiet
              ? 'text-v2-12 font-medium text-v2-fg-3'
              : clsx(
                  'font-semibold text-v2-fg',
                  titleClassName ?? 'text-v2-13',
                ),
          )}
        >
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 max-w-[68ch] text-v2-12 leading-[18px] text-v2-fg-3">
            {description}
          </p>
        )}
      </div>
      {control && (
        <div className="flex shrink-0 items-center gap-2">{control}</div>
      )}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

/**
 * Reading width for a settings section. The workspace stays full width;
 * the form column stops where the content stays comfortable.
 */
export type SectionMeasure =
  | 'overview'
  | 'voice'
  | 'hours'
  | 'events'
  | 'reservations'
  | 'notifications'
  | 'full';

const MEASURE_CLASS: Record<SectionMeasure, string> = {
  overview: 'max-w-[1060px]',
  voice: 'max-w-[820px]',
  hours: 'max-w-[1040px]',
  events: 'max-w-[880px]',
  // Month grid beside a fixed upcoming column. Wider than a form so day
  // cells can show the area name.
  reservations: 'max-w-[1320px]',
  notifications: 'max-w-[860px]',
  full: 'max-w-none',
};

/**
 * Body wrapper for a section route: gutter, top padding, content cap.
 * `footer` renders outside the cap so a `SaveBar` spans the whole column and
 * sticks to the bottom of the scroll area.
 */
export const SectionBody: React.FC<{
  children: React.ReactNode;
  wide?: boolean;
  measure?: SectionMeasure;
  footer?: React.ReactNode;
}> = ({ children, wide, measure, footer }) =>
  useEmbedded() ? (
    <div>
      {children}
      {footer}
    </div>
  ) : (
    <div className="flex min-h-full flex-col">
      <div
        className={clsx(
          'flex-1 px-4 pb-10 pt-5 lg:px-6',
          measure
            ? MEASURE_CLASS[measure]
            : wide
              ? 'max-w-[1240px]'
              : 'max-w-[960px]',
        )}
      >
        {children}
      </div>
      {footer}
    </div>
  );

/** Skeleton lines standing in for a block's content. */
export const BlockSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className="flex flex-col gap-2.5" aria-busy>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} width={`${72 - i * 14}%`} />
    ))}
  </div>
);

/** The part of a React Query result that `QueryGate` reads. */
export interface GateQuery<T> {
  status: 'pending' | 'error' | 'success';
  data: T | undefined;
  error?: unknown;
  refetch: () => unknown;
}

const messageFor = (error: unknown, fallback: string) => {
  if (typeof error === 'string' && error) return error;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/**
 * Renders loading / error (with Retry) / content for one query. The error is
 * a danger `Callout` where the content would be, per the "feedback inline, no
 * toasts" rule.
 */
export function QueryGate<T>({
  query,
  errorTitle = 'Something went wrong.',
  skeleton,
  children,
}: {
  query: GateQuery<T>;
  errorTitle?: string;
  skeleton?: React.ReactNode;
  children: (data: T) => React.ReactNode;
}) {
  if (query.status === 'pending') return <>{skeleton ?? <BlockSkeleton />}</>;
  if (query.status === 'error' || query.data === undefined) {
    return (
      <Callout tone="danger" title={messageFor(query.error, errorTitle)}>
        <div className="mt-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              query.refetch();
            }}
          >
            Retry
          </Button>
        </div>
      </Callout>
    );
  }
  return <>{children(query.data)}</>;
}

/** "Saved." in `success` beside the control, gone after 2.5s. */
export const SavedNote: React.FC<{
  when: number | null;
  children?: React.ReactNode;
}> = ({ when, children = 'Saved.' }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!when) return;
    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), 2500);
    return () => window.clearTimeout(t);
  }, [when]);
  if (!visible) return null;
  return (
    <span role="status" className="v2-fade-in text-v2-12 text-v2-success">
      {children}
    </span>
  );
};

/** Track a "saved at" timestamp for `SavedNote`. */
export function useSavedAt(): [number | null, () => void] {
  const [at, setAt] = useState<number | null>(null);
  return [at, useCallback(() => setAt(Date.now()), [])];
}

/** Two-line contact/identity cell: strong first line, quiet second. */
export const TwoLine: React.FC<{
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  className?: string;
}> = ({ primary, secondary, className }) => (
  <span className={clsx('flex min-w-0 flex-col leading-4', className)}>
    <span className="truncate font-medium text-v2-fg">{primary}</span>
    {secondary && (
      <span className="truncate text-v2-12 text-v2-fg-3">{secondary}</span>
    )}
  </span>
);
